import React, { useState, MouseEvent, useEffect } from 'react';
import NavLink from 'next/link';
import WavyLink, { WavyIconSize } from './WavyLink';
import top_right_arrow_icon from '../../public/imgs/icons/top_right_arrow_icon.svg';
import hyperlink_icon from '../../public/imgs/icons/hyperlink_icon.svg';
import hyperlink_icon_white from '../../public/imgs/icons/hyperlink_icon_white.svg';
import download_icon from '../../public/imgs/icons/download_icon.svg';
import share_icon from '../../public/imgs/icons/share_icon.svg';
import { motion, useAnimation } from 'framer-motion';
import styles from './Links.module.scss';
import { useMediaQuery } from 'react-responsive';

export enum LinkType {
  WAVY_ARROW = 'wavy_arrow',
  TOP_RIGHT_ARROW_ICON = 'top_right_arrow_icon',
  HYPERLINK_ICON = 'hyperlink_icon',
  HYPERLINK_ICON_WHITE = 'hyperlink_icon_white',
  TOP_ARROW_ICON = 'top_arrow_icon',
  BOTTOM_ARROW_ICON = 'bottom_arrow_icon',
  LEFT_ARROW_ICON = 'left_arrow_icon',
  RIGHT_ARROW_ICON = 'right_arrow_icon',
  DOWNLOAD_ICON = 'download_icon',
  SHARE_ICON = 'share_icon',
}

export enum LinkSizeIcon {
  EXTRALARGE = 'extra_large_icon',
  LARGE = 'large_icon',
  XXMEDIUM = 'xxmedium_icon',
  XMEDIUM = 'xmedium_icon',
  MEDIUM = 'medium_icon',
  SMALL = 'small_icon',
  DEFAULT = 'default_icon',
}

export enum LinkSizeText {
  LARGE = 'largeTextLink',
  MEDIUM = 'mediumTextLink',
  SMALL = 'smallTextLink',
  DEFAULT = 'mediumTextLink',
}

export enum LinkFontWeight {
  NORMAL = 'normal',
  BOLD = 'bold',
}

export const iconSrc = {
  [LinkType.TOP_RIGHT_ARROW_ICON]:
    top_right_arrow_icon.src + '#' + LinkType.TOP_RIGHT_ARROW_ICON,
  [LinkType.HYPERLINK_ICON]: hyperlink_icon.src + '#' + LinkType.HYPERLINK_ICON,
  [LinkType.HYPERLINK_ICON_WHITE]:
    hyperlink_icon_white.src + '#' + LinkType.HYPERLINK_ICON,
  [LinkType.TOP_ARROW_ICON]:
    top_right_arrow_icon.src + '#' + LinkType.TOP_RIGHT_ARROW_ICON,
  [LinkType.BOTTOM_ARROW_ICON]:
    top_right_arrow_icon.src + '#' + LinkType.TOP_RIGHT_ARROW_ICON,
  [LinkType.LEFT_ARROW_ICON]:
    top_right_arrow_icon.src + '#' + LinkType.TOP_RIGHT_ARROW_ICON,
  [LinkType.RIGHT_ARROW_ICON]:
    top_right_arrow_icon.src + '#' + LinkType.TOP_RIGHT_ARROW_ICON,
  [LinkType.DOWNLOAD_ICON]: download_icon.src + '#' + LinkType.DOWNLOAD_ICON,
  [LinkType.SHARE_ICON]: `${share_icon.src}#${LinkType.SHARE_ICON}`,
};

type Props = {
  href?: string;
  type?: LinkType;
  iconSize?: LinkSizeIcon;
  textSize?: LinkSizeText;
  isIconFirst?: boolean;
  children: JSX.Element | JSX.Element[] | string | string[];
  fontWeight?: LinkFontWeight;
  isExternal?: boolean;
  className?: string;
};

let timer: number | undefined;
const isTopRightArrow = (type?: LinkType) =>
  type === LinkType.TOP_RIGHT_ARROW_ICON ||
  type === LinkType.TOP_ARROW_ICON ||
  type === LinkType.BOTTOM_ARROW_ICON ||
  type === LinkType.LEFT_ARROW_ICON ||
  type === LinkType.RIGHT_ARROW_ICON;

const Link = (props: Props): JSX.Element => {
  const isDesktop = useMediaQuery({ query: '(min-width: 900px)' });
  const {
    href,
    type,
    iconSize = LinkSizeIcon.DEFAULT,
    textSize = LinkSizeText.DEFAULT,
    isIconFirst = false,
    children,
    fontWeight,
    isExternal,
    className,
  } = props;
  const controls = useAnimation();
  const iconControls = useAnimation();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isTopRightArrowAniming, setIsTopRightArrowAniming] = useState(false);

  const showToast = (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    if (type === LinkType.HYPERLINK_ICON) {
      e.preventDefault();
      if (href && !timer) {
        navigator.clipboard.writeText(`${window.location.host}${href}`);
        controls.set({
          x: isDesktop ? '100%' : undefined,
          y: !isDesktop ? '-300%' : undefined,
        });
        controls.start({
          x: isDesktop ? '0%' : undefined,
          y: !isDesktop ? '-40px' : undefined,
          transition: {
            duration: 0.35,
            ease: [0.19, 0.6, 0, 1],
          },
        });
        timer = window.setTimeout(() => {
          const animValues = {
            x: isDesktop ? '100%' : undefined,
            y: !isDesktop ? '-300%' : undefined,
          };
          controls.start({
            ...animValues,
            transition: {
              duration: 0.35,
              ease: [0.19, 0.6, 0, 1],
            },
          });
          if (timer) {
            clearTimeout(timer);
            timer = undefined;
          }
        }, 3000);
      }
    }
  };

  const startAnimation = async () => {
    if (isMouseOver || isTopRightArrowAniming) return;
    setIsMouseOver(true);
    setIsTopRightArrowAniming(true);

    await iconControls.start({
      x: ['0%', '100%', '-100%', '0%'],
      y: ['0%', '-100%', '100%', '0%'],
      transition: {
        duration: 0.6,
        times: [0, 0.3, 0.3, 0.6],
      },
    });
    setIsTopRightArrowAniming(false);
  };

  const onMouseOver = () => {
    startAnimation();
  };

  const onMouseOut = () => {
    setIsMouseOver(false);
  };

  useEffect(() => {
    if (type !== LinkType.HYPERLINK_ICON) return;
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    controls.set({
      x: isDesktop ? '100%' : undefined,
      y: !isDesktop ? '-300%' : undefined,
    });
    return () => {
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    };
  }, [isDesktop]);

  const btnContent = (
    <>
      {type === LinkType.WAVY_ARROW ? (
        <WavyLink
          isIconFirst={isIconFirst}
          iconClassName={styles.icon}
          size={
            iconSize === LinkSizeIcon.SMALL ? WavyIconSize.SMALL : undefined
          }
        >
          {children}
        </WavyLink>
      ) : (
        <>
          <span>{children}</span>
          <span
            className={`${styles.icon} ${
              type === LinkType.HYPERLINK_ICON ? styles.hyperlink : ''
            } ${styles[iconSize]}`}
          >
            {type && (
              <motion.svg animate={iconControls} custom={isMouseOver}>
                <use href={iconSrc[type]}></use>
              </motion.svg>
            )}
          </span>
        </>
      )}
    </>
  );

  if (!href)
    return (
      <div
        className={`${textSize || ''} ${styles.btn} ${
          fontWeight ? `font-weight-${fontWeight}` : ''
        } ${className || ''}`}
        onClick={type === LinkType.HYPERLINK_ICON ? showToast : undefined}
        onMouseEnter={isTopRightArrow(type) ? onMouseOver : undefined}
        onMouseLeave={isTopRightArrow(type) ? onMouseOut : undefined}
      >
        {btnContent}
      </div>
    );

  return (
    <>
      <NavLink
        href={
          href.includes('mailto:')
            ? href.replace('http://', '').replace('https://', '')
            : href || ''
        }
        passHref={true}
        legacyBehavior={true}
      >
        <a
          className={`${textSize || ''} ${styles.btn} ${
            fontWeight ? `font-weight-${fontWeight}` : ''
          } ${className || ''}`}
          onClick={type === LinkType.HYPERLINK_ICON ? showToast : undefined}
          onMouseOver={isTopRightArrow(type) ? onMouseOver : undefined}
          onMouseLeave={isTopRightArrow(type) ? onMouseOut : undefined}
          target={
            isExternal && !href.includes('mailto:') ? '_blank' : undefined
          }
        >
          {btnContent}
        </a>
      </NavLink>
      {type === LinkType.HYPERLINK_ICON && (
        <motion.div
          transformTemplate={({ x, y }) => {
            const translateY = y ? `translateY(${y})` : '';
            const translateX = x ? `translateX(${x})` : '';
            return `${translateY} ${translateX}`;
          }}
          className={`${styles.toast}`}
          initial={{
            x: isDesktop ? '100%' : '0%',
            y: !isDesktop ? '-100%' : '0%',
          }}
          animate={controls}
        >
          <div>
            <span className={type ? styles[LinkSizeIcon.XXMEDIUM] : undefined}>
              {type && (
                <svg>
                  <use href={iconSrc[LinkType.HYPERLINK_ICON_WHITE]}></use>
                </svg>
              )}
            </span>
            <div className={`text_4 ${styles.text_4}`}>
              Link copied. Spread it wisely!
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Link;
